import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { NavLink } from '@/components/NavLink';
import { useAuth } from '@/hooks/useAuth';
import {
  LayoutDashboard,
  Users,
  Mail,
  CreditCard,
  ClipboardList,
  BarChart3,
  Shield,
  LogOut,
  MessageSquare,
  ChevronRight,
  Home,
  Bug,
  Target,
  Megaphone,
  MessageCircle,
  GraduationCap,
  FileText,
  Download,
  Lock,
  SlidersHorizontal,
  ClipboardCheck,
  BookOpen,
  ShieldCheck,
  Activity,
  Settings2,
} from 'lucide-react';
import munilexLogo from '@/assets/munilex-logo.png';
import { useEffect, useMemo } from 'react';
import { prefetchArea } from '@/lib/routePrefetch';

type Role = 'user' | 'admin' | 'super_admin';

interface AdminNavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: Role[];
  /** Match also nested routes (e.g. /admin/oposiciones/*) */
  matchPrefix?: boolean;
}

interface AdminNavGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: AdminNavItem[];
}

const NAV_GROUPS: AdminNavGroup[] = [
  {
    id: 'inicio',
    label: 'Inicio',
    icon: Home,
    items: [
      {
        path: '/admin/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        roles: ['admin', 'super_admin'],
      },
    ],
  },
  {
    id: 'personas',
    label: 'Personas',
    icon: Users,
    items: [
      { path: '/admin/users', label: 'Usuarios', icon: Users, roles: ['admin', 'super_admin'] },
      { path: '/admin/consultas', label: 'Consultas', icon: Users, roles: ['admin', 'super_admin'] },
      { path: '/admin/messages', label: 'Mensajes recibidos', icon: Mail, roles: ['admin', 'super_admin'] },
      { path: '/admin/sales-marketing-ai', label: 'Sales & Marketing AI', icon: Target, roles: ['admin', 'super_admin'], matchPrefix: true },
      { path: '/admin/billing', label: 'Facturación', icon: CreditCard, roles: ['admin', 'super_admin'] },
    ],
  },
  {
    id: 'oposiciones',
    label: 'Oposiciones',
    icon: ClipboardList,
    items: [
      {
        path: '/admin/questions',
        label: 'Banco de Preguntas',
        icon: ClipboardList,
        roles: ['admin', 'super_admin'],
      },
      {
        path: '/admin/academic-audit',
        label: 'Auditoría académica',
        icon: ShieldCheck,
        roles: ['admin', 'super_admin'],
      },
      {
        path: '/admin/oposiciones/actividad',
        label: 'Actividad Oposiciones',
        icon: Activity,
        roles: ['admin', 'super_admin'],
      },
      {
        path: '/admin/oposiciones/contenido',
        label: 'Contenido Oposiciones',
        icon: BookOpen,
        roles: ['admin', 'super_admin'],
        matchPrefix: true,
      },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    icon: Shield,
    items: [
      { path: '/admin/usage', label: 'Métricas de uso', icon: BarChart3, roles: ['admin', 'super_admin'] },
      { path: '/admin/models', label: 'Modelos de documento', icon: FileText, roles: ['admin', 'super_admin'] },
      { path: '/admin/export', label: 'Exportaciones', icon: Download, roles: ['admin', 'super_admin'] },
      { path: '/admin/security', label: 'Seguridad', icon: Shield, roles: ['super_admin'] },
      { path: '/admin/settings/plans', label: 'Planes (límites free)', icon: Settings2, roles: ['super_admin'] },
      { path: '/admin/errors', label: 'Errores del cliente', icon: Bug, roles: ['admin', 'super_admin'] },
    ],
  },
];

function AdminSidebar() {
  const { profile } = useAuth();
  const location = useLocation();
  const role = (profile?.role || 'user') as Role;
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  // Filter groups by role (hide group entirely if all items hidden)
  const visibleGroups = useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((it) => it.roles.includes(role)),
      })).filter((g) => g.items.length > 0),
    [role],
  );

  const isActive = (item: AdminNavItem) =>
    item.matchPrefix ? location.pathname.startsWith(item.path) : location.pathname === item.path;

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <Link to="/admin/dashboard" className="flex items-center gap-3">
          <img src={munilexLogo} alt="Munilex" className="h-8 w-auto shrink-0" />
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="font-semibold text-sidebar-foreground">Munilex</span>
              <span className="text-xs text-sidebar-foreground/60">Panel Admin</span>
            </div>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {visibleGroups.map((group, idx) => (
          <div key={group.id}>
            {idx > 0 && <SidebarSeparator />}
            <SidebarGroup>
              {!collapsed && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item);
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                          <NavLink
                            to={item.path}
                            className="flex items-center gap-3"
                            activeClassName="font-medium"
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                            <span>{item.label}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <FooterActions collapsed={collapsed} />
      </SidebarFooter>
    </Sidebar>
  );
}

function FooterActions({ collapsed }: { collapsed: boolean }) {
  const { signOut, profile } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login');
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Ir al Chat">
          <Link to="/app/chat" className="flex items-center gap-3">
            <MessageSquare className="h-4 w-4 shrink-0" />
            <span>Ir al Chat</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {!collapsed && profile?.email && (
        <div className="px-3 py-1.5 text-xs text-sidebar-foreground/60 truncate" title={profile.email}>
          {profile.email}
        </div>
      )}
      <SidebarMenuItem>
        <SidebarMenuButton onClick={handleSignOut} tooltip="Cerrar sesión">
          <LogOut className="h-4 w-4 shrink-0" />
          <span>Cerrar sesión</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function Breadcrumbs() {
  const location = useLocation();
  const crumbs = useMemo(() => {
    const trail: Array<{ label: string; href?: string }> = [{ label: 'Admin', href: '/admin/dashboard' }];
    for (const group of NAV_GROUPS) {
      const item = group.items.find((it) =>
        it.matchPrefix ? location.pathname.startsWith(it.path) : location.pathname === it.path,
      );
      if (item) {
        if (group.id !== 'inicio') trail.push({ label: group.label });
        trail.push({ label: item.label });
        break;
      }
    }
    return trail;
  }, [location.pathname]);

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-muted-foreground">
      {crumbs.map((c, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5 opacity-50" />}
          {c.href ? (
            <Link to={c.href} className="hover:text-foreground transition-colors">
              {c.label}
            </Link>
          ) : (
            <span className={i === crumbs.length - 1 ? 'text-foreground font-medium' : ''}>{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function AdminLayout() {
  // Prefetch en background de las páginas hermanas del panel admin.
  useEffect(() => { prefetchArea('admin'); }, []);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AdminSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-12 flex items-center gap-3 border-b border-border bg-card/60 backdrop-blur px-4 sticky top-0 z-10">
            <SidebarTrigger />
            <Breadcrumbs />
          </header>
          <main className="flex-1 overflow-auto">
            <div className="p-6 lg:p-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

